<template>
  <div class="font-dropdown" ref="dropdown">
    <button 
      class="font-bbl-dropdown-trigger"
      @click="toggleDropdown"
      :class="{ 'is-open': isOpen }"
    >
      <span class="font-preview" :style="{ fontFamily: currentValue || 'inherit' }">
        {{ currentLabel }}
      </span>
      <svg class="dropdown-icon" :class="{ 'rotate': isOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    
    <transition name="dropdown-fade">
      <div v-if="isOpen" class="font-bbl-dropdown-menu">
        <div class="bbl-dropdown-search">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search fonts..."
            class="bbl-search-input"
            @click.stop
          />
        </div>
        <div class="bbl-dropdown-menu-inner">
          <button
            v-for="option in filteredOptions"
            :key="option.value"
            class="font-option"
            :class="{ 'is-active': currentValue === option.value }"
            @click="selectOption(option)"
          >
            <span class="option-preview" :style="{ fontFamily: option.value || 'inherit' }">
              {{ option.label }}
            </span>
            <svg v-if="currentValue === option.value" class="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div v-if="filteredOptions.length === 0" class="no-results">
            No fonts found
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'FontFamilyDropdown',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      options: [
        { value: '', label: 'Default Font' },
        { value: 'Arial, sans-serif', label: 'Arial' },
        { value: "'Helvetica Neue', Helvetica, sans-serif", label: 'Helvetica' },
        { value: "'Times New Roman', Times, serif", label: 'Times New Roman' },
        { value: 'Georgia, serif', label: 'Georgia' },
        { value: "'Courier New', Courier, monospace", label: 'Courier New' },
        { value: 'Verdana, sans-serif', label: 'Verdana' },
        { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
        { value: "'Comic Sans MS', cursive", label: 'Comic Sans MS' },
        { value: 'Impact, Charcoal, sans-serif', label: 'Impact' },
        { value: "'Lucida Sans Unicode', 'Lucida Grande', sans-serif", label: 'Lucida Sans' },
        { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
        { value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif", label: 'Palatino' },
        { value: 'Garamond, serif', label: 'Garamond' },
        { value: "'Bookman Old Style', serif", label: 'Bookman' },
        { value: "'Arial Black', Gadget, sans-serif", label: 'Arial Black' },
        { value: "'Brush Script MT', cursive", label: 'Brush Script' },
        { value: "'Copperplate', 'Copperplate Gothic Light', fantasy", label: 'Copperplate' },
        { value: 'Papyrus, fantasy', label: 'Papyrus' },
        { value: "'Lucida Console', Monaco, monospace", label: 'Lucida Console' },
        { value: "'Andale Mono', monospace", label: 'Andale Mono' },
        { value: "'Consolas', monospace", label: 'Consolas' },
        { value: "'Monaco', monospace", label: 'Monaco' },
        { value: "'Roboto', sans-serif", label: 'Roboto' },
        { value: "'Open Sans', sans-serif", label: 'Open Sans' },
        { value: "'Lato', sans-serif", label: 'Lato' },
        { value: "'Montserrat', sans-serif", label: 'Montserrat' },
        { value: "'Poppins', sans-serif", label: 'Poppins' },
        { value: "'Source Sans Pro', sans-serif", label: 'Source Sans Pro' },
        { value: "'Raleway', sans-serif", label: 'Raleway' },
        { value: "'PT Sans', sans-serif", label: 'PT Sans' },
        { value: "'Merriweather', serif", label: 'Merriweather' },
        { value: "'Playfair Display', serif", label: 'Playfair Display' },
        { value: "'Noto Sans', sans-serif", label: 'Noto Sans' },
        { value: "'Ubuntu', sans-serif", label: 'Ubuntu' },
        { value: "'Nunito', sans-serif", label: 'Nunito' },
        { value: "'Oswald', sans-serif", label: 'Oswald' },
        { value: "'Quicksand', sans-serif", label: 'Quicksand' },
        { value: "'Fira Sans', sans-serif", label: 'Fira Sans' },
        { value: "'Inter', sans-serif", label: 'Inter' },
        { value: "'Work Sans', sans-serif", label: 'Work Sans' }
      ]
    }
  },
  computed: {
    currentValue() {
      return this.modelValue || ''
    },
    currentLabel() {
      const option = this.options.find(opt => opt.value === this.currentValue)
      return option ? option.label : 'Default Font'
    },
    filteredOptions() {
      if (!this.searchQuery) return this.options
      const query = this.searchQuery.toLowerCase()
      return this.options.filter(opt => 
        opt.label.toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.searchQuery = ''
        this.$nextTick(() => {
          const input = this.$el.querySelector('.bbl-search-input')
          if (input) input.focus()
        })
      }
    },
    selectOption(option) {
      this.$emit('update:modelValue', option.value)
      this.$emit('change', { target: { value: option.value } })
      this.isOpen = false
      this.searchQuery = ''
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false
        this.searchQuery = ''
      }
    }
  }
}
</script>

<style scoped>
.font-dropdown {
  position: relative;
  display: inline-block;
}

.font-bbl-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 12px;
  min-width: 150px;
  height: 32px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.font-bbl-dropdown-trigger:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.font-bbl-dropdown-trigger.is-open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: white;
}

.font-preview {
  flex: 1;
  text-align: left;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-icon {
  color: #6b7280;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.font-bbl-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 240px;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  overflow: hidden;
}

.bbl-dropdown-search {
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
}

.bbl-search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
}

.bbl-search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.bbl-search-input::placeholder {
  color: #9ca3af;
}

.bbl-dropdown-menu-inner {
  padding: 6px;
  max-height: 320px;
  overflow-y: auto;
}

.font-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  color: #374151;
  font-weight: 400;
}

.font-option:hover {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.font-option.is-active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  font-weight: 500;
}

.option-preview {
  flex: 1;
  font-size: 14px;
}

.check-icon {
  color: #3b82f6;
  flex-shrink: 0;
  margin-left: 8px;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}

/* Dropdown animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Scrollbar styling */
.bbl-dropdown-menu-inner::-webkit-scrollbar {
  width: 6px;
}

.bbl-dropdown-menu-inner::-webkit-scrollbar-track {
  background: transparent;
}

.bbl-dropdown-menu-inner::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.bbl-dropdown-menu-inner::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
